<script lang="ts">
  /**
   * HistoryRings — the two-ring summary atop the scores drawer (was atop the
   * Scores tab until the tabs slice, 2026-09-10).
   *
   * Extracted clean from the (now-unmounted) MetricRings 4-ring scroller rather
   * than trimmed in place: this view needs exactly TWO rings, centered, in a
   * borderless calm card — not a horizontal snap strip. It reuses the SAME
   * aggregate fns (weekDistanceMeters / weeksInARow / effectiveWeekTargetM)
   * and the same SVG ring markup, and keeps the two load-bearing behaviors:
   *   - tap the distance ring to set the weekly target (goal-setting, NOT
   *     gamification): `onEditGoal`, the drawer's inline form;
   *   - never a stale target: it reads the shared `goalPrefs` store, which a
   *     save updates at once and App.svelte refreshes when the tab appears.
   *
   * Ring 2 is a CONSECUTIVE-WEEK streak ("Weeks in a row") — an explicit,
   * scoped owner decision (2026-07-07) that reverses this component's earlier
   * "never a consecutive streak" stance for the Scores surface. The framing
   * stays soft: the in-progress week never breaks the run (weeksInARow's
   * grace), a 0 reads as a warm fresh start, and nothing ever says "lost".
   * No day-streak, no peak-HR, no scores/badges beyond countable facts.
   *
   * Arc honesty (persona gate 2026-07-10, 5/5 panelists): a partial arc IS a
   * claim of "% of a target". The distance ring draws its arc ONLY when a
   * real user-set weekly goal exists, and names the denominator in a subline
   * ("of 10 km"); with no goal set it shows the plain number on the neutral
   * track — never a fraction of a default the user never chose. The streak
   * ring is not a ring at all: a streak has no denominator, and two look-alike
   * circles that behaved differently scanned as a bug (persona gate 2026-07-10,
   * B3). It renders as a plain big number beside the goal ring — the one ring
   * on the page is the only thing shaped like a gauge.
   */
  import type { WorkoutSummary } from "@contract/types";
  import { weekDistanceMeters, weeksInARow } from "../lib/aggregates";
  import { goalPrefs } from "../lib/overlay";
  import { haptic } from "../lib/haptics";

  // onEditGoal: the drawer's inline goal form (no sheet on a sheet).
  let { workouts, onEditGoal }: { workouts: WorkoutSummary[]; onEditGoal: () => void } = $props();

  let distanceM = $derived(weekDistanceMeters(workouts));
  let weeksRow = $derived(weeksInARow(workouts));

  // The arc only means something against a goal the USER set (null = unset).
  // No goal → no arc; never a fraction of DEFAULT_WEEK_TARGET_M (see doc
  // comment). The shared goal store is loaded by App.svelte and set on save.
  let userWeekTargetM = $derived($goalPrefs?.weekDistanceMeters ?? null);
  let distanceFill = $derived(
    userWeekTargetM !== null && userWeekTargetM > 0
      ? Math.min(1, distanceM / userWeekTargetM)
      : 0,
  );

  // The arc's denominator, said in plain words. Meters only — every distance
  // on the Scores page is in meters; "of 3 km" over a "2,850 m" value forced a
  // km→m conversion in the page's largest text (persona gate 2026-07-10, B1).
  function fmtGoalShort(m: number): string {
    return `of ${m.toLocaleString()} m`;
  }

  // Most common real state (Monday / rest weeks): no swims THIS week.
  let emptyWeek = $derived(distanceM === 0);

  // Ring center: comma-formatted whole meters ("6,050") with a smaller trailing
  // "m". The full "6,050 m" string is too wide for the 96px ring, so the unit
  // trails at a smaller size. 5-digit weeks scale the number down a step.
  let distanceWhole = $derived(Math.round(distanceM));
  let distanceLabel = $derived(distanceWhole.toLocaleString());
  let distanceOverflow = $derived(distanceWhole >= 10000);

  // Circumference of an r=42 circle (2πr ≈ 263.9) — the dasharray reference.
  const CIRC = 263.9;

  function openDistanceGoal() {
    haptic("selection");
    onEditGoal();
  }
</script>

<div class="ring-card" role="region" aria-label="This week's swimming">
  <div class="rings">
    <!-- LEFT: distance this week. Tappable to configure the weekly target. -->
    <button
      type="button"
      class="ring-btn"
      aria-label={userWeekTargetM === null ? "Set a weekly goal" : "Change your weekly goal"}
      onclick={openDistanceGoal}
    >
      <div class="ring">
        <!-- 112px (was 96): with the 24px hero value, the r=42 circle's inner
             span at 96px left "2,850" grazing the stroke on both sides. The
             matched cells give the ring room, so the gauge grows instead of
             the number shrinking further. -->
        <svg viewBox="0 0 100 100" width="112" height="112" aria-hidden="true">
          <defs>
            <linearGradient id="dist-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="#FF7A45" />
              <stop offset="100%" stop-color="#FBC02D" />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r="42" fill="none"
                  stroke="rgba(255,255,255,0.10)" stroke-width="8" />
          {#if !emptyWeek && userWeekTargetM !== null}
            <circle cx="50" cy="50" r="42" fill="none"
                    stroke="url(#dist-grad)" stroke-width="8" stroke-linecap="round"
                    stroke-dasharray={`${distanceFill * CIRC} ${CIRC}`}
                    transform="rotate(-90 50 50)" />
          {/if}
        </svg>
        <div class="center">
          {#if emptyWeek}
            <div class="empty-center">No swim yet</div>
          {:else}
            <div class="value" class:value-lg={distanceOverflow}>{distanceLabel}{#if userWeekTargetM === null}<span class="unit">m</span>{/if}</div>
            {#if userWeekTargetM !== null}
              <!-- B1: one unit on the page. With a goal set, the "m" lives on
                   the denominator so the ring reads "2,850 / of 3,000 m" (one
                   unit), never meters-over-km. No goal → the value keeps its
                   own "m". -->
              <div class="subline">{fmtGoalShort(userWeekTargetM)}</div>
            {/if}
          {/if}
        </div>
      </div>
      <div class="label">This week</div>
    </button>

    <!-- RIGHT: consecutive weeks with a swim (owner decision 2026-07-07).
         NOT a ring — a streak has no denominator, and two look-alike circles
         reading two ways scanned as a bug (persona gate 2026-07-10, B3). But a
         BARE number next to the gauge then read as an unfinished tile that lost
         its ring (persona gate round 4, 5/5). Fix: both stats sit in matching
         subtle cells so the pair reads as two siblings of one family, one of
         which happens to carry a gauge. The "weeks" subline is gone — the
         "WEEKS IN A ROW" label already says it (was printed twice). Warm at
         zero: a fresh start, never a "lost streak". -->
    <div class="streak">
      <div class="streak-figure">
        {#if weeksRow === 0}
          <div class="empty-center">Fresh start</div>
        {:else}
          <div class="value">{weeksRow}</div>
        {/if}
      </div>
      <div class="label">{weeksRow === 1 ? "Week in a row" : "Weeks in a row"}</div>
    </div>
  </div>
</div>

<style>
  .ring-card {
    background: var(--bg-card);
    border: 0;
    border-radius: var(--radius-card);
    padding: var(--space-5) var(--space-4);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
    /* Same calm Dawn-Glass blur family as the other History cards. */
    backdrop-filter: blur(22px) saturate(1.2);
    -webkit-backdrop-filter: blur(22px) saturate(1.2);
  }
  .rings {
    display: flex;
    justify-content: center;
    gap: var(--space-3);
    width: 100%;
    /* Load-bearing: explicit pan-x so the global body `touch-action: manipulation`
       does not eat horizontal swipe-snap navigation in the WKWebView. Trap #2 from
       the WKWebView research — never `none` on a region that can be panned. */
    touch-action: pan-x;
  }

  /* Both stats live in matching subtle cells (persona gate round 4): the gauge
     and the streak read as two siblings of one family, so the ringless streak
     no longer looks like a tile that lost its ring. Same size, padding, bg. */
  .ring-btn,
  .streak {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-4) var(--space-2);
    background: rgba(255, 255, 255, 0.04);
    border-radius: var(--radius-card);
  }

  .ring-btn {
    border: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .ring-btn:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 4px;
    border-radius: var(--radius-card);
  }

  .ring {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 96px;
  }
  .ring svg { display: block; }

  /* Streak figure — a 112px box (matching the ring's footprint) holding the plain
     counted number, so the two cells stay the same height. Reuses .value /
     .empty-center / .label so the typography matches the ring exactly. */
  .streak-figure {
    width: 112px;
    height: 112px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
  }

  /* Center stack sits over the 112px ring (svg is 112 tall). */
  .center {
    position: absolute;
    top: 0;
    left: 0;
    width: 112px;
    height: 112px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
  }
  /* 24px (was 28): a real inner safety margin so a 4-digit "2,850" + "of
     3,000 m" clear the ring stroke on both sides (persona gate round 4, B3 —
     the hero number was grazing its own gauge). Still the largest number in
     the two cells; the colored gauge gives it hero prominence beyond size. */
  .value {
    font-family: "Lexend", system-ui, sans-serif;
    font-size: 24px;
    line-height: 1;
    font-weight: 600;
    color: #E4E2E4;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
    display: flex;
    align-items: baseline;
    gap: 1px;
  }
  .value .unit {
    font-size: 14px;
    font-weight: 500;
    color: #C5C6CC;
  }
  .subline {
    font-size: 14px;
    color: #C5C6CC;
    font-variant-numeric: tabular-nums;
  }
  /* 5-digit weeks (>= 10,000 m) would overrun the 96px ring; drop a step. */
  .value.value-lg {
    font-size: 19px;
  }
  .empty-center {
    font-size: 15px;
    color: #C5C6CC;
    text-align: center;
    padding: 0 8px;
    line-height: 1.2;
  }
  .label {
    /* Label tier (12px uppercase) — the ring's big number carries the meaning;
       this caption sits a clear step below it, matching the card-title spec in
       DESIGN.md rather than competing with body text. (Literal #ABACB4 → the
       canonical muted token, opportunistic DESIGN.md cleanup 2026-07-10.) */
    margin-top: var(--space-2);
    font-size: 12px;
    color: var(--text-muted);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-align: center;
    white-space: nowrap;
  }
</style>
